/**
 * Category and sub-category for templates and pipelines.
 *
 * Customers will choose which kinds of communication they receive, so every
 * template and pipeline says what kind it is. A sub-category is only valid
 * under the categories that fit it (no payment notices under Marketing).
 *
 * The API owns the list (GET /comms/v2/template-categories, loaded once at
 * start-up through applyCategoryPayload). The lists below are only the
 * fallback until that answers, or when it cannot:
 * services/CommunicationManagementService/models/template_categories.py
 */

export const CATEGORIES = [
    { label: 'Utility', value: 'UTILITY' },
    { label: 'Marketing', value: 'MARKETING' },
    { label: 'Authentication', value: 'AUTHENTICATION' },
]

export const SUB_CATEGORIES = [
    {
        label: 'Class and session reminders',
        value: 'class_session_reminders',
        categories: ['UTILITY', 'MARKETING'],
    },
    {
        label: 'Payments and billing',
        value: 'payments_billing',
        categories: ['UTILITY'],
    },
    {
        label: 'Account and service notices',
        value: 'account_service_notices',
        categories: ['UTILITY', 'MARKETING', 'AUTHENTICATION'],
    },
    {
        label: 'Progress and learning updates',
        value: 'progress_learning_updates',
        categories: ['UTILITY', 'MARKETING'],
    },
    {
        label: 'Webinars, masterclasses and events',
        value: 'webinars_masterclasses_events',
        categories: ['UTILITY', 'MARKETING'],
    },
    {
        label: 'Offers, news and greetings',
        value: 'offers_news_greetings',
        categories: ['MARKETING'],
    },
    {
        label: 'Internal ops and testing',
        value: 'internal_ops_testing',
        categories: ['UTILITY', 'MARKETING'],
    },
    // A catch-all chosen by hand when nothing else fits; allowed under every category.
    {
        label: 'Others',
        value: 'others',
        categories: ['UTILITY', 'MARKETING', 'AUTHENTICATION'],
    },
]

let slackCategories = ['UTILITY', 'MARKETING']

const titleCase = (value) => value.charAt(0) + value.slice(1).toLowerCase()

/**
 * Replace the fallback lists with what the API sent. The arrays are updated
 * in place, so every importer sees the new list. A payload that is not the
 * expected shape is ignored.
 */
export const applyCategoryPayload = (payload) => {
    const subs = payload?.sub_categories
    if (
        !Array.isArray(payload?.categories) ||
        !Array.isArray(subs) ||
        subs.length === 0
    )
        return
    CATEGORIES.splice(
        0,
        CATEGORIES.length,
        ...payload.categories.map((value) => ({
            label: titleCase(value),
            value,
        }))
    )
    SUB_CATEGORIES.splice(
        0,
        SUB_CATEGORIES.length,
        ...subs.map(({ label, value, categories }) => ({
            label,
            value,
            categories,
        }))
    )
    if (Array.isArray(payload.slack_categories))
        slackCategories = payload.slack_categories
}

/** Categories a channel can use. Slack is Utility or Marketing only. */
export const categoriesForCommType = (commType) =>
    commType === 'slack'
        ? CATEGORIES.filter((c) => slackCategories.includes(c.value))
        : CATEGORIES

/** Pipelines send through several channels; Authentication needs all of them to allow it. */
export const categoriesForCommTypes = (commTypes = []) =>
    commTypes.includes('slack') ? categoriesForCommType('slack') : CATEGORIES

/** Sub-categories that fit a category. Empty until a category is chosen. */
// WhatsApp's category is Meta's and can change after approval, so any
// sub-category fits any category there (the service applies the same rule).
export const subCategoriesForCategory = (category, commType) =>
    !category
        ? []
        : commType === 'whatsapp'
        ? SUB_CATEGORIES
        : SUB_CATEGORIES.filter((s) => s.categories.includes(category))

export const subCategoryLabel = (value) =>
    SUB_CATEGORIES.find((s) => s.value === value)?.label ?? ''

/** True when both are set and fit each other. */
export const isValidCategoryPair = (category, subCategory, commType) =>
    !!category &&
    !!subCategory &&
    subCategoriesForCategory(category, commType).some(
        (s) => s.value === subCategory
    )

/** Field errors for the pair, keyed `category` / `subCategory`. Empty when valid. */
export const validateCategoryPair = (category, subCategory, commType) => {
    const errors = {}
    if (!category) errors.category = 'Choose a category'
    if (!subCategory) errors.subCategory = 'Choose a sub-category'
    else if (category && !isValidCategoryPair(category, subCategory, commType))
        errors.subCategory = 'This sub-category does not fit the category'
    return errors
}
